import { z } from 'zod';

/**
 * Block Schema — the typed content model for Posts and Layouts (ADR 0033).
 *
 * A document's body is an ordered array of blocks instead of one Quill-HTML
 * string. `richtext` carries Quill-native HTML and is the migration bridge:
 * legacy `html` maps losslessly to `[{ type: 'richtext', html }]`.
 *
 * Storage: Mongoose persists `blocks` as `[Mixed]` (toMongooseSchema has no
 * union support), so THIS schema, enforced with `safeParse` at the API
 * controller boundary, is the only validation. Every write that carries
 * `blocks` must go through it.
 *
 * `id` is a client-generated stable identifier (crypto.randomUUID()) used for
 * drag-and-drop keys and autosave diffing — never generated server-side.
 *
 * Columns nest exactly one level (a column holds ChildBlocks, never another
 * columns block). This is a deliberate constraint, not an oversight:
 * z.discriminatedUnion cannot hold a z.lazy member, and one level is all the
 * editor's layout needs.
 */

const blockBase = { id: z.string().min(1) };

export const RichtextBlockSchema = z.object({
  ...blockBase,
  type: z.literal('richtext'),
  // Quill-native HTML — same contract as the legacy Post.html / Layout.html,
  // rendered inside `.ql-editor` so packages/shared/styles/quillContent.css
  // keeps governing typography on both apps.
  html: z.string(),
});

export const ImageBlockSchema = z.object({
  ...blockBase,
  type: z.literal('image'),
  src: z.string().min(1),
  alt: z.string().default(''),
  width: z.enum(['full', 'medium', 'small']).default('full'),
  // Free resize (canvas drag handle / settings slider). When set it OVERRIDES
  // the `width` preset — percent of the containing column. Optional with no
  // default: absent means "use the preset", so pre-existing blocks render
  // unchanged.
  widthPct: z.number().min(10).max(100).optional(),
  alignment: z.enum(['left', 'center', 'right']).default('center'),
  link: z.string().optional(),
});

export const EmbedBlockSchema = z.object({
  ...blockBase,
  type: z.literal('embed'),
  // The URL the author pasted (YouTube / Vimeo / Maps). The renderer derives
  // the iframe src from it — the raw author input is what's stored.
  url: z.string().min(1),
});

export const DividerBlockSchema = z.object({
  ...blockBase,
  type: z.literal('divider'),
});

export const ButtonBlockSchema = z.object({
  ...blockBase,
  type: z.literal('button'),
  label: z.string().min(1),
  url: z.string().min(1),
  style: z.enum(['primary', 'outline']).default('primary'),
  alignment: z.enum(['left', 'center', 'right']).default('left'),
});

export const ChildBlockSchema = z.discriminatedUnion('type', [
  RichtextBlockSchema,
  ImageBlockSchema,
  EmbedBlockSchema,
  DividerBlockSchema,
  ButtonBlockSchema,
]);

export const ColumnsBlockSchema = z.object({
  ...blockBase,
  type: z.literal('columns'),
  columns: z
    .array(
      z.object({
        id: z.string().min(1),
        // Relative width in fr units (default 1 → equal columns). Per-column
        // rather than a block-level ratio string so it survives column
        // add/remove and validates structurally. [1,2] = 33/67, [2,1,1] =
        // 50/25/25, etc. Applied only ≥640px — mobile always stacks.
        span: z.number().min(1).max(4).optional(),
        blocks: z.array(ChildBlockSchema),
      })
    )
    .min(2)
    .max(4),
});

export const BlockSchema = z.discriminatedUnion('type', [
  RichtextBlockSchema,
  ImageBlockSchema,
  EmbedBlockSchema,
  DividerBlockSchema,
  ButtonBlockSchema,
  ColumnsBlockSchema,
]);

export const BlocksSchema = z.array(BlockSchema);

/**
 * Post cover image, extracted from the legacy lead-image convention by the
 * html→blocks migration (or set directly in the canvas editor). Optional with
 * no default — absent means "no cover", and on unmigrated posts the public
 * renderer falls back to the render-time extractCover() convention.
 */
export const CoverImageSchema = z.object({
  src: z.string().min(1),
  alt: z.string().default(''),
});

export type Block = z.infer<typeof BlockSchema>;
export type ChildBlock = z.infer<typeof ChildBlockSchema>;
export type RichtextBlock = z.infer<typeof RichtextBlockSchema>;
export type ImageBlock = z.infer<typeof ImageBlockSchema>;
export type EmbedBlock = z.infer<typeof EmbedBlockSchema>;
export type DividerBlock = z.infer<typeof DividerBlockSchema>;
export type ButtonBlock = z.infer<typeof ButtonBlockSchema>;
export type ColumnsBlock = z.infer<typeof ColumnsBlockSchema>;
export type CoverImage = z.infer<typeof CoverImageSchema>;
